import type { CatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type {
    CatalogImportLineResult,
    CatalogImportLineStatus,
    CatalogImportMatch,
    CatalogImportProblem,
    CatalogImportResult,
} from '@/api/models';
import {
    noticeFor,
    toneFor,
    type CatalogImportNotice,
    type CatalogImportNoticeTone,
} from '@/pages/catalogImport/notices';
import {
    isBarcodeFixCode,
    variantKeyOf,
    type ParsedLine,
} from '@/pages/catalogImport/store';
import { tetriFromDecimal } from '@/utils/money';

/**
 * What a row's status chip says: the preview's status, `excluded` for a row
 * the user left out since, or `unchecked` for a row the last check left out
 * and the user included since (no answer until the next check).
 */
export type ImportRowStatus = CatalogImportLineStatus | 'unchecked';

/** What a row's note says: one sentence and the tone it is shown in. */
export type ImportRowNote = {
    notice: CatalogImportNotice;
    tone: CatalogImportNoticeTone;
    /**
     * The note is a barcode case the owner chooses per product (bos-app#232:
     * an IMEI, a number Excel scrambled, a code that is not a number): its
     * link reads Choose. For an IMEI, `imeiRows` is how many rows of the
     * row's variant hold one, and the note says so instead of the notice
     * ("Barcode is an IMEI. 13 rows of this product hold IMEIs."); `null`
     * for the other cases.
     */
    choice?: { imeiRows: number | null };
};

/**
 * One row of the Products table: a parsed line of the file joined with the
 * preview's answer for it. Read-only; built once per preview and kept per
 * row (`buildRows` reuses a row whose inputs did not change).
 */
export type ImportRow = {
    /** The Excel row number: the row's identity and the table's first column. */
    rowNumber: number;
    /** The preview's status, `excluded` as soon as the user leaves the row out, `unchecked` once included again after a check left it out. */
    status: ImportRowStatus;
    /** Whether the user left the row out (`exclusions`), checked or not: the row's Exclude / Include. */
    isExcluded: boolean;
    name: string;
    sku: string;
    /** The barcode cell as the file has it, trimmed; empty when blank (the import then takes the SKU). */
    barcode: string;
    /** The lot number cell as the file has it, trimmed (an IMEI or a serial); empty when blank. */
    lotNumber: string;
    /** How many rows make up the row's product (`groupRows`) when more than one; else `null`. */
    groupSize: number | null;
    /** The row's variation cells, `Color: Black · Storage: 128 GB`; empty when it has none. */
    attributes: string;
    quantity: number | null;
    costTetri: number | null;
    salePriceTetri: number | null;
    /** What the row does to the catalog; `null` when unmatched or excluded, as the server answers an excluded row. */
    match: CatalogImportMatch | null;
    /** The first problem, else the first warning; `null` when the row has neither. */
    note: ImportRowNote | null;
    /** The parsed line the row shows (the drawer edits it). */
    line: ParsedLine;
    /** The preview's answer for the row. */
    result: CatalogImportLineResult;
};

/**
 * What a row's one control does: leave the row out of the import, or take it
 * back. The table and the phone cards both hand them to `RowExclusionButton`.
 */
export type ImportRowExclusionHandlers = {
    onExcludeRow: (rowNumber: number) => void;
    onIncludeRow: (rowNumber: number) => void;
};

/**
 * What a row's Edit button and its note's Fix link do: open the row in the
 * drawer, as pressing the row does. The table and the phone cards hand it to
 * `RowEditButton` and `NoteCell`.
 */
export type ImportRowEditHandler = {
    onEditRow: (rowNumber: number) => void;
};

/**
 * A row whose note offers Fix: the last check found a problem on it (what
 * blocks it) and the user has not left it out. A row with only warnings
 * needs nothing, and an excluded row's note says what it had without asking
 * for it.
 */
export const isFlaggedRow = (row: ImportRow): boolean =>
    !row.isExcluded && row.result.problems.length > 0;

/** The separator between a row's attributes. */
export const ATTRIBUTE_SEPARATOR = ' · ';

/** The row's variation cells as the file has them, in column order. */
export const attributesOf = (line: ParsedLine): string =>
    (line.optionValues ?? [])
        .map(({ attributeTypeName, valueText }) => {
            const value = valueText?.trim() ?? '';
            const type = attributeTypeName?.trim() ?? '';
            if (value === '') {
                return '';
            }
            return type === '' ? value : `${type}: ${value}`;
        })
        .filter((part) => part !== '')
        .join(ATTRIBUTE_SEPARATOR);

const noteFrom = (problem: CatalogImportProblem): ImportRowNote => ({
    notice: noticeFor(problem),
    tone: toneFor(problem.code),
});

const BARCODE_LOOKS_LIKE_IMEI =
    'BARCODE_LOOKS_LIKE_IMEI' satisfies CatalogImportCode;

/**
 * The one thing the note column says about a row: its first problem (what
 * blocks it) over its first warning (what the import will do on its own).
 * A barcode case is a choice (`ImportRowNote.choice`); an IMEI's says how
 * many rows of its variant hold IMEIs (`imeiRows`, the row itself when not
 * given).
 */
export const noteOf = (
    result: CatalogImportLineResult,
    imeiRows = 1,
): ImportRowNote | null => {
    const problem = result.problems[0];
    if (problem && isBarcodeFixCode(problem.code)) {
        return {
            ...noteFrom(problem),
            choice: {
                imeiRows:
                    problem.code === BARCODE_LOOKS_LIKE_IMEI ? imeiRows : null,
            },
        };
    }
    const first = problem ?? result.warnings[0];
    return first ? noteFrom(first) : null;
};

const hasImei = (result: CatalogImportLineResult): boolean =>
    result.problems.some((problem) => problem.code === BARCODE_LOOKS_LIKE_IMEI);

/**
 * How many rows of each variant (`variantKeyOf`) hold an IMEI the last
 * check found, the user's excluded rows left out.
 */
const imeiRowsByVariant = (
    lineByRow: ReadonlyMap<number, ParsedLine>,
    preview: CatalogImportResult,
    exclusions: ReadonlySet<number>,
): Map<string, number> => {
    const counts = new Map<string, number>();
    for (const result of preview.lines) {
        const line = lineByRow.get(result.rowNumber);
        if (!line || exclusions.has(result.rowNumber) || !hasImei(result)) {
            continue;
        }
        const key = variantKeyOf(line, result);
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
};

/**
 * Everything the last check said about a row: every problem, then every
 * warning, each as a sentence with its tone. The row drawer's problem card
 * lists them; the note column shows the first (`noteOf`).
 */
export const notesOf = (result: CatalogImportLineResult): ImportRowNote[] => [
    ...result.problems.map(noteFrom),
    ...result.warnings.map(noteFrom),
];

const tetriOrNull = (value: number | null | undefined): number | null =>
    typeof value === 'number' && Number.isFinite(value)
        ? tetriFromDecimal(value)
        : null;

const numberOrNull = (value: number | null | undefined): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The user's exclusion decides first; a row the last check left out that the
 * user did not is one included since, with no answer yet. The row drawer's
 * header reads it too.
 */
export const statusOf = (
    result: CatalogImportLineResult,
    isExcluded: boolean,
): ImportRowStatus => {
    if (isExcluded) {
        return 'excluded';
    }
    return result.status === 'excluded' ? 'unchecked' : result.status;
};

const rowOf = (
    line: ParsedLine,
    result: CatalogImportLineResult,
    isExcluded: boolean,
    imeiRows: number,
): ImportRow => ({
    rowNumber: result.rowNumber,
    status: statusOf(result, isExcluded),
    isExcluded,
    name: line.name?.trim() ?? '',
    sku: line.sku?.trim() ?? '',
    barcode: line.barcode?.trim() ?? '',
    lotNumber: line.lotNumber?.trim() ?? '',
    groupSize: result.groupRows.length > 1 ? result.groupRows.length : null,
    attributes: attributesOf(line),
    quantity: numberOrNull(line.quantity),
    costTetri: tetriOrNull(line.cost),
    salePriceTetri: tetriOrNull(line.salePrice),
    match: isExcluded ? null : result.match,
    note: noteOf(result, imeiRows),
    line,
    result,
});

/**
 * The table's rows, one per line of the preview, in its order (the request's,
 * which is the file's). Each joins the parsed line of the same row number; a
 * line the preview did not answer for has no row.
 *
 * `previous` is the last build: a row whose parsed line, answer, exclusion
 * and IMEI count are the same as before is returned as it was, so an edit
 * or an exclusion re-renders that row alone (and the IMEI rows of its
 * variant, whose count it changes). A new preview answers every row anew.
 */
export const buildRows = (
    lines: readonly ParsedLine[],
    preview: CatalogImportResult | null,
    exclusions: ReadonlySet<number>,
    previous: readonly ImportRow[] = [],
): ImportRow[] => {
    if (!preview) {
        return [];
    }
    const lineByRow = new Map(lines.map((line) => [line.rowNumber, line]));
    const previousByRow = new Map(previous.map((row) => [row.rowNumber, row]));
    const imeiCounts = imeiRowsByVariant(lineByRow, preview, exclusions);

    return preview.lines.flatMap((result) => {
        const line = lineByRow.get(result.rowNumber);
        if (!line) {
            return [];
        }
        const isExcluded = exclusions.has(result.rowNumber);
        const imeiRows = hasImei(result)
            ? (imeiCounts.get(variantKeyOf(line, result)) ?? 1)
            : 1;
        const before = previousByRow.get(result.rowNumber);
        if (
            before &&
            before.line === line &&
            before.result === result &&
            before.isExcluded === isExcluded &&
            (before.note?.choice?.imeiRows ?? null) ===
                (noteOf(result, imeiRows)?.choice?.imeiRows ?? null)
        ) {
            return [before];
        }
        return [rowOf(line, result, isExcluded, imeiRows)];
    });
};

/**
 * A `buildRows` that remembers its last build, so each call reuses the rows
 * that did not change. One per table: the hook keeps it for its lifetime.
 */
export const createRowsBuilder = () => {
    let last: ImportRow[] = [];
    return (
        lines: readonly ParsedLine[],
        preview: CatalogImportResult | null,
        exclusions: ReadonlySet<number>,
    ): ImportRow[] => {
        last = buildRows(lines, preview, exclusions, last);
        return last;
    };
};
